"use client";

import React from "react";
import { ArrowRight, Beaker, CircleCheck as CheckCircle2, FileCheck2, FlaskConical, Microscope, TestTubes } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const applicationsData = [{
  id: "pharma-analysis",
  title: "Pharmaceutical Analysis",
  badge: "Formulation & Release",
  description: "Assay testing, impurity profiling, and active pharmaceutical ingredient (API) quantitation for batch release and formulation integrity.",
  iconName: "FlaskConical",
  details: "High-performance liquid chromatography (HPLC) and ultra-high-performance systems supporting validated assay protocols in regulated laboratory environments.",
  linkPath: "/PharmaceuticalManufacturingPage",
  linkLabel: "Explore pharmaceutical workflows"
}, {
  id: "chemical-verification",
  title: "Chemical Verification",
  badge: "Purity & ID",
  description: "Identification of unknown compounds, solvent purity checks, and residual contaminant analysis across continuous and batch processing.",
  iconName: "TestTubes",
  details: "Gas and liquid chromatography separation paired with standard detectors to confirm structural properties and chemical purity margins.",
  linkPath: "/ChemicalManufacturingPage",
  linkLabel: "View chemical applications"
}, {
  id: "compliance-testing",
  title: "Regulatory Compliance Testing",
  badge: "QA / QC Standards",
  description: "Standardized methodology validation aligned to internal laboratory quality procedures.",
  iconName: "FileCheck2",
  details: "Audit-ready data generation and system suitability tracking to support method transfer between laboratories.",
  linkPath: "/QualificationLifecycleServicesPage",
  linkLabel: "Review compliance services"
}, {
  id: "lab-research",
  title: "Laboratory Research",
  badge: "Method Development",
  description: "Exploratory compound separation, column screening, and process scale-up testing across academic and industrial R&D facilities.",
  iconName: "Microscope",
  details: "Flexible analytical parameters for method optimization, kinetic studies, and complex matrix characterization.",
  linkPath: "/AcademicAndResearchPage",
  linkLabel: "See research capabilities"
}];
const capabilityHighlights = [{
  title: "Method Transfer Support",
  description: "Support for transferring analytical methods between laboratory scale and routine testing."
}, {
  title: "Data Integrity & Traceability",
  description: "Workflows aligned with internal data integrity procedures and audit trail practices."
}, {
  title: "Multi-Detector Compatibility",
  description: "Configuration options for UV/Vis, refractive index, mass spectrometry, and fluorescence detection."
}];
export default function ChromatographyApplications() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "FlaskConical":
        return <FlaskConical className="size-6 text-primary" />;
      case "TestTubes":
        return <TestTubes className="size-6 text-primary" />;
      case "FileCheck2":
        return <FileCheck2 className="size-6 text-primary" />;
      case "Microscope":
        return <Microscope className="size-6 text-primary" />;
      default:
        return <Beaker className="size-6 text-primary" />;
    }
  };
  return <section id="chromatographyapplications" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Header Section */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3">
            Analytical Focus
          </Badge>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground mb-4">
            Chromatography Applications Across Regulated Sectors
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Analytical separation techniques configured for routine quality control, regulated compliance verification, and method development across Irish and UK facilities.
          </p>
        </div>

        {/* 4-Card Application Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-16">
          {applicationsData.map((app, i) => <motion.div key={app.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.1
        }} className="flex">
              <Card className="flex-1 flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="p-2.5 rounded-lg bg-secondary inline-flex items-center justify-center">
                      {getIcon(app.iconName)}
                    </div>
                    <Badge variant="outline" className="text-muted-foreground border-border">
                      {app.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl md:text-2xl font-semibold text-foreground pt-2">
                    {app.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm md:text-base leading-relaxed">
                    {app.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-2 flex flex-col gap-6">
                  <div className="p-3.5 rounded-md bg-secondary/50 border border-border/60 text-xs md:text-sm text-foreground/90 leading-relaxed">
                    <span className="font-medium text-foreground block mb-1">Analytical Scope:</span>
                    {app.details}
                  </div>

                  <div className="pt-2">
                    <Button asChild variant="ghost" className="px-0 hover:bg-transparent text-primary hover:text-primary/90 font-medium group justify-start">
                      <Link to={app.linkPath} className="inline-flex items-center gap-1.5 text-sm">
                        <span>{app.linkLabel}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Supporting Application Attributes Banner */}
        <div className="rounded-xl border border-border bg-secondary/40 p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 divide-y md:divide-y-0 md:divide-x divide-border">
            {capabilityHighlights.map((highlight, i) => <div key={highlight.title} data-index={i} className={`flex flex-col gap-2 ${i > 0 ? "pt-6 md:pt-0 md:pl-8" : ""}`}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <h3 className="font-medium text-foreground text-base">
                    {highlight.title}
                  </h3>
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {highlight.description}
                </p>
              </div>)}
          </div>
        </div>

        {/* Section Bottom Consultation CTA */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 rounded-lg border border-border bg-background">
          <div>
            <p className="font-medium text-foreground text-base">
              Need technical review for a specific separation challenge?
            </p>
            <p className="text-sm text-muted-foreground">
              Speak with an application specialist regarding column choice, instrumentation, or method validation.
            </p>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <Button asChild variant="default" className="w-full sm:w-auto">
              <Link to="/DiscussYourApplicationPage">
                Discuss Your Application
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto">
              <Link to="/ApplicationConsultationPage">
                Consultation Details
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}